import type {InterceptableRequest, InterceptableResponse} from 'theoplayer';

import {EventDispatcher, Subscribable} from '../../core/EventDispatcher';
import {HttpRequestType} from '../../features/httprequesttracking/HttpRequestType';
import {OnDownloadFinishedEventObject} from '../../features/httprequesttracking/OnDownloadFinishedEventObject';
import {getGlobalPerformanceNow} from '../../utils/globalScopeUtils';
import {logger} from '../../utils/Logger';
import {isNumber, parseIntOrUndefined} from '../../utils/numberUtils';
import {endsWith, isBlank, isValidString} from '../../utils/stringUtils';
import {isArrayBuffer, isNotNullish, isNullish} from '../../utils/Utils';

import {THEOplayerProxy} from './THEOplayerProxy';

export const STALE_REQUEST_TIMEOUT_MS = 3 * 60 * 1_000; // three minutes

interface PendingRequest {
  url: string;
  startTime: number;
  type: string | undefined;
  subType: string | undefined;
  mediaType: string | undefined;
}

/**
 * Unified network interceptor for THEOplayer that captures HTTP request/response data
 * and publishes standardized {@link OnDownloadFinishedEventObject} events.
 *
 * This allows both {@link DownloadSpeedMeter} and {@link HttpRequestTracking} to subscribe
 * to a single source of network events.
 */
export class THEOplayerNetworkInterceptor implements Subscribable<OnDownloadFinishedEventObject> {
  private eventDispatcher = new EventDispatcher<OnDownloadFinishedEventObject>();
  private pendingRequests = new Map<string, PendingRequest[]>();

  constructor(private player: THEOplayerProxy) {
    if (isNullish(this.player.network)) {
      logger.warn('THEOplayerNetworkInterceptor: player.network is not available');
      return;
    }

    this.player.network.addRequestInterceptor(this.onRequest);
    this.player.network.addResponseInterceptor(this.onResponse);
  }

  subscribe(callback: (args: OnDownloadFinishedEventObject) => void): () => void {
    return this.eventDispatcher.subscribe(callback);
  }

  unsubscribe(callback: (args: OnDownloadFinishedEventObject) => void): void {
    this.eventDispatcher.unsubscribe(callback);
  }

  reset(): void {
    this.pendingRequests.clear();
  }

  private onRequest = (request: InterceptableRequest): void => {
    try {
      const url = request.url;
      // we need to double-check the url as a valid string, because our implementation is using it as a key in the map,
      // and if it's invalid, it can cause issues
      if (!isValidString(url) || isBlank(url)) {
        return;
      }

      const performanceNow = getGlobalPerformanceNow();
      const now = performanceNow ?? Date.now();

      // clean up any stale pending requests that have been pending for too long before adding the new request
      // to prevent map overflow in case of missing responses or other issues
      // No need for logic with timeout here, let's keep it simple
      this.cleanupStaleRequests(now);

      const pendingRequest: PendingRequest = {
        url: url,
        startTime: now,
        type: request.type,
        subType: request.subType,
        mediaType: request.mediaType,
      };

      const existing = this.pendingRequests.get(url);
      if (isNullish(existing)) {
        this.pendingRequests.set(url, [pendingRequest]);
      } else {
        existing.push(pendingRequest);
      }
    } catch (error) {
      logger.error('THEOplayerNetworkInterceptor: Error in request interceptor', error);
    }
  };

  private onResponse = (response: InterceptableResponse): void => {
    try {
      const request = response.request;
      // if the original request is missing, we can't correlate the response to a pending request, so we skip processing
      if (isNullish(request)) {
        return;
      }

      const url = request.url;
      // we need to double-check the url as a valid string, because our implementation is using it as a key in the map,
      // and if it's invalid, it can cause issues
      if (!isValidString(url) || isBlank(url)) {
        return;
      }

      const pendingArray = this.pendingRequests.get(url);
      if (isNullish(pendingArray) || pendingArray.length === 0) {
        return;
      }

      // Use FIFO: remove the oldest (first) pending request
      const pending = pendingArray.shift()!;

      if (pendingArray.length === 0) {
        this.pendingRequests.delete(url);
      }

      const performanceNow = getGlobalPerformanceNow();
      const endTime = performanceNow ?? Date.now();

      // round the final download time to a whole number to avoid unnecessary precision (performance.now() returns a high-precision timestamp as double)
      const downloadTime = Math.round(endTime - pending.startTime);

      const size = extractResponseSize(response);
      const httpStatus: number = isNumber(response.status) ? response.status : 0;
      const success = httpStatus >= 200 && httpStatus < 400;

      this.eventDispatcher.dispatch({
        httpRequest: {
          downloadTime: downloadTime,
          httpStatus: httpStatus,
          success: success,
          timestamp: Date.now(),
          url: url,
          size: size,
          timeToFirstByte: undefined, // missing in THEOplayer network API
          type: mapHttpRequestType(pending.type, pending.subType, pending.mediaType, url),
        },
      });
    } catch (error) {
      logger.error('THEOplayerNetworkInterceptor: Error in response interceptor', error);
    }
  };

  private cleanupStaleRequests(now: number): void {
    this.pendingRequests.forEach((pendingArray: PendingRequest[], url: string) => {
      const filtered = pendingArray.filter(
        (pending: PendingRequest) => now - pending.startTime <= STALE_REQUEST_TIMEOUT_MS,
      );

      if (filtered.length === 0) {
        this.pendingRequests.delete(url);
      } else if (filtered.length !== pendingArray.length) {
        this.pendingRequests.set(url, filtered);
      }
    });
  }
}

export function extractResponseSize(response: InterceptableResponse): number | undefined {
  const contentLength = response.headers?.['content-length'] ?? response.headers?.['Content-Length'];
  if (isNotNullish(contentLength)) {
    const parsed = parseIntOrUndefined(contentLength);
    if (isNotNullish(parsed)) {
      return parsed;
    }
  }

  if (isNotNullish(response.body)) {
    if (isValidString(response.body)) {
      return response.body.length;
    }
    if (isArrayBuffer(response.body) && isNumber(response.body.byteLength)) {
      return response.body.byteLength;
    }
  }

  return undefined;
}

export function mapHttpRequestType(
  requestType: string | undefined,
  requestSubType: string | undefined,
  mediaType: string | undefined,
  url: string,
): HttpRequestType {
  // Check for content protection (DRM) requests first
  if (requestType === 'content-protection') {
    switch (requestSubType) {
      case 'widevine-license':
        return HttpRequestType.DRM_LICENSE_WIDEVINE;
      case 'fairplay-license':
        return HttpRequestType.DRM_LICENSE_FAIRPLAY;
      case 'playready-license':
        return HttpRequestType.DRM_LICENSE_PLAYREADY;
      case 'clearkey-license':
        return HttpRequestType.DRM_LICENSE_CLEARKEY;
      case 'aes128-key':
        return HttpRequestType.KEY_HLS_AES;
      case 'fairplay-certificate':
        return HttpRequestType.DRM_CERTIFICATE_FAIRPLAY;
      case 'widevine-certificate':
        // no enum value for this category on HttpRequestType - needs update from player team
        return HttpRequestType.UNKNOWN;
      default:
        return HttpRequestType.UNKNOWN;
    }
  }

  // Check for manifest requests based on URL extension
  if (requestType === 'manifest') {
    if (endsWith(url, '.mpd')) {
      return HttpRequestType.MANIFEST_DASH;
    }
    if (endsWith(url, '.m3u8')) {
      return HttpRequestType.MANIFEST_HLS_MASTER;
    }
    return HttpRequestType.UNKNOWN;
  }

  // Check for media segment requests based on mediaType
  if (mediaType === 'video') {
    return HttpRequestType.MEDIA_VIDEO;
  }
  if (mediaType === 'audio') {
    return HttpRequestType.MEDIA_AUDIO;
  }
  if (mediaType === 'text') {
    return HttpRequestType.MEDIA_SUBTITLES;
  }

  // Fallback: check URL extension for manifests even without explicit type
  if (endsWith(url, '.mpd')) {
    return HttpRequestType.MANIFEST_DASH;
  }
  if (endsWith(url, '.m3u8')) {
    return HttpRequestType.MANIFEST_HLS_MASTER;
  }

  return HttpRequestType.UNKNOWN;
}
